/**
 * AtlasAvatarModule — local avatar prediction/reconciliation + remote
 * interpolation + the first-person camera rig.
 *
 * Movement is server-authoritative: each fixed tick this module integrates the
 * local intent with the SAME math the server uses (6 m/s, fixed dt) for
 * responsive prediction, then sends the `move` reducer. When the authoritative
 * `player_state` for self diverges past a threshold, it snaps (full input
 * replay is a later milestone). Remote avatars are lerped toward their latest
 * authoritative transform each frame.
 */

import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { GROUND_FAR_PLANE_M } from '../lib/terrain_far_tier.js';
import { cloudTunnel } from '../lib/cloud_tunnel.js';
import { warmThenShow } from '../lib/pipeline_warm.js';
import { Logger } from 'enginex/core/Logger.js';
import { bus } from '../bus.js';
import { makeSystem, Phase } from '../engine-compat.js';
import { MOVE_SPEED_MPS, FIXED_DT } from '../config.js';
import {
  moveSpeedFor, slideActive, dashActive, dashAllowed,
  MODE_WALK, MODE_SPRINT, MODE_SLIDE, MODE_SWIM, MODE_CROUCH, MODE_DASH, type MoveMode,
} from '../lib/combat_core.js';
import { feel, stepFeel, cameraImpulse, readShakeSetting, SPRINT_FOV_DEG, SLIDE_EYE_DROP_M, CROUCH_EYE_DROP_M, DASH_FOV_DEG } from '../lib/combat_feel.js';
import { sfx } from '../lib/sfx.js';
/** An idle turn past this many radians is flushed to the server (facing law). */
const IDLE_YAW_FLUSH_RAD = 0.08;
/** …at most this often while standing still. */
const IDLE_YAW_FLUSH_MS = 250;
import { freshMoveHealth, noteSend, noteResolve, noteRejection, observeAck } from '../lib/move_watchdog.js';
import { freshReconcile, recordSend, decideCorrection, applySmoothing, logicalX, logicalZ, type ReconcileState } from '../lib/move_reconcile.js';
import { groundYIn } from '../lib/grounding.js';
import { enterables, clampIndoor } from '../lib/building_interiors.js';
import { buildAvatar, type AvatarProxy } from '../avatar/AvatarFactory.js';
import { isThirdPerson, footCameraPose, hoverCameraPose, cameraRayDir, hoverCameraRayDir, collideCamBack, smoothCamBack, CAMERA_PAD_M, CAMERA_MIN_BACK_M } from '../lib/foot_camera.js';
import { rayGroundHit } from '../lib/impact_decals.js';
import { modelKeyOf, prefabAssetId } from '../lib/player_models.js';
import { activePreset } from '../lib/quality_tiers.js';
import { windDragFactor, type WindSample } from '../lib/wind_physics.js';
import type { AssetModule } from './AssetModule.js';
import { defaultAppearance } from '@shared/avatar.js';
import type { PhysicsModule } from './PhysicsModule.js';

export const EYE_HEIGHT = 1.6; // desktop rig eye offset; AtlasXRModule zeroes it while presenting
interface NudgeStats { sent: number; accepted: number; rejected: number; reverted: number; timedOut: number; lastError: string }

const RECONCILE_SNAP_M = 0.25;
/** A crouched body (mine in third person, every crouched remote) squats to this height fraction. */
const CROUCH_BODY_SCALE = 0.72;
/** Floor for a remote RIDER's drawn height above the ground beneath it — the
 *  skimmer deck. The authoritative posY is the truth; this only keeps a rider
 *  off the dirt while the viewer's terrain for that spot is still streaming. */
const RIDE_DECK_M = 0.9;
const RECONCILE_HARD_SNAP_M = 4; // beyond this = a real teleport, not drift
// Wave-2 input coalescing: predict every 60 Hz tick, SEND every Nth (the Valve
// ~30 cmd-packets/sec model) via the moveSteps reducer. 2 ticks ≈ 0.2 m of window,
// under the 0.25 m reconcile snap even on a full direction reversal.
const SEND_EVERY_TICKS = 2;

export class AtlasAvatarModule implements IModule {
  readonly name = 'AtlasAvatar';

  private scene!: THREE.Scene;
  private camera!: THREE.PerspectiveCamera;
  private localBody!: THREE.Object3D;

  /** Remote avatar render proxies, keyed by identity hex. */
  private remoteProxies = new Map<string, AvatarProxy>();
  /** COMBAT WAVE movement grammar — the client mirror of the server's mode
   *  rule (combat_core): a slide needs a sprint and decays over SLIDE_MS. */
  private mode: MoveMode = MODE_WALK;
  private slideStartedMs = -1;
  /** Dash (build-now wave 3): when the last burst began (−∞ = never) and its committed direction. */
  private dashStartedMs = -Infinity;
  private dashDirX = 0;
  private dashDirZ = -1;
  /** Crouch (build-now wave 2): the player's WISH; the capsule may lag it under a ceiling. */
  private wantCrouch = false;
  /** Guard (build-now wave 4): the state the server last heard (null = resend). */
  private guardSent: boolean | null = false;
  /** Fixed steps actually simulated (probes measure speed in SIM time — a
   *  swiftshader browser runs the loop far below wall-clock rate). */
  private fixedTicks = 0;
  /** Probe-only fixed-step trace (mode + speed per step); null = off, no cost. */
  private trace: Array<{ t: number; mode: number; v: number }> | null = null;
  /** The yaw the server last heard (idle turns flush it — see fixed()). */
  private lastSentYaw = 0;
  private lastIdleYawFlushMs = -Infinity;
  /** null = not yet probed: moveSteps2 exists only after the combat-wave publish+regen. */
  private canMode: boolean | null = null;
  /**
   * The weather's wind at the player, or null when there is no WeatherModule
   * (headless harnesses, a personal world before the module is up).
   */
  private windSample(): WindSample | null {
    const m = this.appRef?.getModule('Weather') as { wind?: () => WindSample } | undefined;
    return m?.wind ? m.wind() : null;
  }

  /** The default on-foot field of view when the player has not chosen one. */
  private static readonly DEFAULT_FOV = 75;
  /**
   * The FOV the combat-feel offsets are applied ON TOP OF: the player's
   * graphics-menu setting when they have one, else the default.
   *
   * WHY a getter (2026-09-14): this was a hardcoded 75, and the per-frame write
   * below (`camera.fov = baseFov + off.fov`) therefore reverted the menu's FOV
   * slider one frame after AtlasGraphicsMenuModule.applyFov set it — the
   * setting appeared to do nothing on foot. Composing the user's base with the
   * transient sprint/slide offset keeps both.
   */
  private get baseFov(): number {
    const deg = activePreset().cameraFov ?? 0;
    return deg > 0 ? deg : AtlasAvatarModule.DEFAULT_FOV;
  }
  private spawned = false;

  // ── Wave-2 input coalescing (accumulated fixed-step window; see flushMove) ──
  private pendingSteps = 0;
  private pendingX = 0;
  private pendingZ = 0;
  private pendingJump = false;
  /** null = not yet probed. moveSteps exists only after the wave-2 publish+regen. */
  private canCoalesce: boolean | null = null;
  // Spawn-stuck watchdog state (see PhysicsModule.unstickAvatar).
  private stuckTicks = 0;       // consecutive fully-blocked move-intent ticks
  private wedgedTicks = 0;      // consecutive blocked ticks with OPPOSING contact normals
  private lastUnstickMs = -Infinity; // relocation rate limit (must exceed the server nudge cooldown)
  private lastRescueMs = -Infinity;  // long-range CAGE escape rate limit (server cooldown 45 s)
  /** Planetfall arrival gate: hold the avatar behind the cloud cover until
   *  REAL collidable ground exists at the arrival spot (field report
   *  2026-08-23: pod ejection seated the player below terrain that streamed
   *  in after the seat). */
  private planetfall: { x: number; z: number; deadline: number; poll: number } | null = null;
  private unstickScanTicks = 0; // slow embedded-capsule scan cadence
  private unstickGraceTicks = 0; // reconcile hold-off while unstickNudge lands

  // ── Movement-health watchdog (the join-freeze lesson) ────────────────────────
  // Decision logic is PURE in lib/move_watchdog.ts (verify:npc covers it);
  // this module just feeds it sends/rejections/acks and voices its verdicts.
  private moveHealth = freshMoveHealth();
  private lastRejectionMsg = '';
  /** Seq-indexed predictions for the lag-aware reconcile (lib/move_reconcile.ts — the walking-drag fix). */
  private readonly recon = freshReconcile();

  private noteSend(): void { noteSend(this.moveHealth, performance.now()); }

  private noteRejection(e: unknown): void {
    this.lastRejectionMsg = String(e).slice(0, 80);
    noteRejection(this.moveHealth);
  }

  /** Called from reconcile() with the authoritative row's inputSeq. */
  private observeAck(serverSeq: number): void {
    const action = observeAck(this.moveHealth, serverSeq, performance.now());
    if (action === 'recovered') {
      bus.emit('atlas:status', { text: 'movement restored — the server is acknowledging inputs again' });
    } else if (action === 'warn-error') {
      bus.emit('atlas:status', { text: `⚠ movement failing: ${this.lastRejectionMsg || 'reducer error'} — check the connection/server` });
    } else if (action === 'warn-stall') {
      bus.emit('atlas:status', { text: `⚠ movement not reaching the server (${this.moveHealth.sendsSinceAck} unacknowledged inputs) — stalled connection? Try reloading` });
    }
  }

  constructor(private readonly physics: PhysicsModule) {}

  private assets: AssetModule | null = null;
  /** Third-person (2026-09-09): my own body under the rig, built from my appearance when the camera first pulls back. */
  private localProxy: AvatarProxy | null = null;
  private localProxyAppearance: unknown = null;
  private localWornId: bigint | null = null;
  private camBackDrawn = 0;
  private thirdPerson = false;
  private readonly _camFrom = { x: 0, y: 0, z: 0 };

  private readonly ac = new AbortController(); // window 'resize' listener scope

  private appRef: App | null = null;

  init(app: App): void {
    this.appRef = app;
    this.scene = app.world.scene;
    this.assets = app.getModule('AssetModule') as AssetModule | null;

    // First-person rig: body carries yaw, camera (child) carries pitch.
    // Far plane 5200: the graphics "max draw distance" wave extends the terrain
    // window + object far-cull to 5 km at the top tiers, so the camera must not
    // clip within that range. WebGPU's reversed-z keeps distant depth precision;
    // on the WebGL2 fallback the exponential fog masks the far range.
    this.camera = new THREE.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      0.05,
      GROUND_FAR_PLANE_M, // the terrain far tier reaches ±98 km; the old 5.2 km plane clipped it (precision is the NEAR plane's)
    );
    this.camera.position.set(0, EYE_HEIGHT, 0);
    this.localBody = new THREE.Object3D();
    this.localBody.add(this.camera);
    this.scene.add(this.localBody);
    atlas.localMesh = this.localBody;
    app.renderer.cameras.add({ camera: this.camera, priority: 0 });
    feel.shakeEnabled = readShakeSetting(); // XAG 117: screen shake is opt-in
    (window as unknown as { __cameraFar?: () => number }).__cameraFar = () => this.camera.far; // draw-distance probe
    (window as unknown as { __moveMode?: () => unknown }).__moveMode = () => this.moveMode; // combat-wave probe: movement grammar
    (window as unknown as { __cameraFov?: () => number }).__cameraFov = () => this.camera.fov; // combat-wave probe: sprint FOV
    (window as unknown as Record<string, unknown>).__footCam = () => { // the scroll-zoom camera (2026-09-09)
      const w = this.camera.getWorldPosition(new THREE.Vector3());
      const vm = this.camera.getObjectByName('weapon-view-model');
      return { camBack: atlas.camBack, hoverCamBack: atlas.hoverCamBack, drawn: this.camBackDrawn, thirdPerson: this.thirdPerson, hover: atlas.hoverRide,
        bodyVisible: this.localProxy?.object.visible ?? null, bodyChildren: this.localProxy?.object.children.length ?? 0, viewModelVisible: vm ? vm.visible : null,
        camLocal: [this.camera.position.x, this.camera.position.y, this.camera.position.z], camWorld: [w.x, w.y, w.z],
        camAbs: [w.x - this.scene.position.x, w.y - this.scene.position.y, w.z - this.scene.position.z], // the FP64 rebase: world = absolute + scene.position
        head: [atlas.localX, atlas.localY + EYE_HEIGHT, atlas.localZ] };
    };
    // combat-wave probe: a per-fixed-step trace — a slide's 900 ms can fall between two swiftshader frames, invisible to any poll
    (window as unknown as { __moveTrace?: unknown }).__moveTrace = { start: () => { this.trace = []; }, stop: () => { const t = this.trace ?? []; this.trace = null; return t; }, read: () => this.trace ?? [] };

    bus.on('atlas:account-ready', () => this.ensureSpawned());
    bus.on('atlas:world-cleared', () => {
      this.clearRemotes();
      this.pendingSteps = 0; this.pendingJump = false; // never flush a stale window into the new world
      // Reset the predicted capsule to spawn — else moveAvatar reads the OLD world's body position
      // back into atlas.localX/Z and clobbers enterWorld's spawn reset until reconcile catches up.
      if (this.physics.ready) this.physics.teleportAvatar(0, 0);
    });
    // Portable identity: when a remote's appearance changes, rebuild its body.
    bus.on('atlas:avatar-updated', ({ identityHex, isLocal }) => {
      if (!isLocal) this.rebuildRemote(identityHex);
    });

    // Planetfall arrival gate: a mid-session hop INTO a ground world (pod
    // ejection, exitSpace, landOnWorld, travel) arrives before the terrain
    // chunk + its stamps stream in — seating then buries the player under
    // the real ground when it lands. Hold behind the cloud cover until the
    // physics world actually has something to stand on there. Flying
    // arrivals (descendToAtmosphere) skip it — the flight path owns them.
    bus.on('atlas:world-adopted', ({ worldId, x, z }) => {
      const conn2 = atlas.conn;
      if (!conn2) return;
      let kind = '';
      for (const w of conn2.db.world.iter()) if (w.worldId === worldId) { kind = w.kind; break; }
      if (kind === 'space') return;
      const me2 = conn2.identity ? conn2.db.playerState.identity.find(conn2.identity) : null;
      if (me2?.animState.startsWith('fly:')) return;
      this.planetfall = { x, z, deadline: performance.now() + 25_000, poll: 0 };
      cloudTunnel.hold(0.6);
    });

    // An authoritative relocation in flight (the sky-ship lift, SkyShipModule):
    // the row is about to jump to where the capsule already stands, so a
    // reconcile tick in between would read the gap as a teleport and snap the
    // capsule back over the OLD row (the fall-out-of-the-ship field bug,
    // 2026-09-03). Hold like the unstick nudge does; settle once it lands.
    // An attack drops the guard (the server does the same on every strike):
    // the button may still be down, but the guard is down until it is
    // pressed again — a swing and a guard cannot share the hand.
    bus.on('atlas:attack', () => { atlas.guardHeld = false; });
    bus.on('atlas:reconcile-hold', ({ ticks, mode }) => {
      this.unstickGraceTicks = mode === 'hold' ? Math.max(this.unstickGraceTicks, ticks) : Math.min(this.unstickGraceTicks, ticks);
    });

    // Prediction + send (FixedSim, before AOI@80 so the AOI window tracks the
    // freshly integrated cell).
    app.world.addSystem(
      makeSystem('AtlasMovement', Phase.FixedSim, 40, () => this.fixedMovement()),
    );
    // Reconcile self against the authoritative row (after the send).
    app.world.addSystem(
      makeSystem('AtlasReconcile', Phase.FixedSim, 75, () => this.reconcile()),
    );
    // Probe hook: drives the full unstick path on demand (headless throttling
    // makes the 2 s scan cadence ~24 s of wall time — too slow to probe).
    (globalThis as unknown as { __unstickNow?: (esc?: boolean, anyClass?: boolean) => { x: number; z: number } | null }).__unstickNow =
      (esc = false, anyClass = false) => this.tryUnstick(esc, anyClass);
    // Probe lever (build-now wave 1): the request queue's pending kinds + the
    // last outcomes, so a probe can prove a press was consumed exactly once,
    // expired on its tick TTL, or was rejected with a reason.
    (globalThis as unknown as { __intents?: () => { tick: number; pending: string[]; log: unknown[] } }).__intents =
      () => ({ tick: this.fixedTicks, pending: atlas.intents.pendingKinds(), log: atlas.intents.log.slice(-16) });
    // Remote interpolation + local transform apply (variable dt).
    app.world.addSystem(
      makeSystem('AtlasInterpolate', Phase.Animation, 36, (dt) => this.interpolate(dt)),
    );

    window.addEventListener('resize', () => {
      this.camera.aspect = window.innerWidth / window.innerHeight;
      this.camera.updateProjectionMatrix();
    }, { signal: this.ac.signal });
  }

  private ensureSpawned(): void {
    this.spawned = true;
  }

  // ─── FixedSim: predict locally + send intent ────────────────────────────────

  private fixedMovement(): void {
    const conn = atlas.conn;
    if (!conn || !this.spawned || atlas.currentWorldId === null) return;
    if (atlas.flyMode || atlas.spaceFlight || atlas.hoverRide || atlas.skyFlight) { this.parkIntents(); return; } // fly cam / space / vehicle owns the rig; the avatar parks
    if (atlas.loadingGate) { this.parkIntents(); return; } // boot loading screen: the world isn't ready — ignore movement intent
    if (this.planetfall) {
      const pf = this.planetfall;
      // Poll readiness at 4 Hz (groundReadyAt steps the physics world).
      const due = ++pf.poll >= 15;
      if (due) pf.poll = 0;
      const ready = due && this.physics.ready && this.physics.groundReadyAt(pf.x, pf.z);
      if (ready || performance.now() > pf.deadline) {
        this.planetfall = null;
        if (this.physics.ready) this.physics.teleportAvatar(pf.x, pf.z); // ray-aware drop-in seat
        atlas.localX = pf.x;
        atlas.localZ = pf.z;
        this.unstickGraceTicks = Math.max(this.unstickGraceTicks, 120); // let the seat settle
        cloudTunnel.reveal(0.6, 2.2);
      } else {
        this.parkIntents();
        return; // held behind the cover — the world under our feet is still loading
      }
    }

    let ix = atlas.intentX;
    let iz = atlas.intentZ;
    const len = Math.hypot(ix, iz);
    const moving = len > 0;
    if (len > 1) {
      ix /= len;
      iz /= len;
    }

    // WIND RESISTANCE (2026-09-14): walking into a sandstorm is harder. This
    // shortens the INTENT VECTOR rather than the speed, because the server
    // integrates the position itself and only clamps intents longer than 1 —
    // a sub-unit intent moves the player proportionally less on BOTH sides, so
    // prediction and authority agree with no reducer change. See
    // lib/wind_physics.ts. Applied here, before `sendX`/`sendZ` and before the
    // physics step, so the collision-resolved send path (which derives its own
    // magnitude from how far the capsule actually moved) inherits it too.
    if (moving) {
      const drag = windDragFactor(ix, iz, this.windSample());
      if (drag < 1) { ix *= drag; iz *= drag; }
    }

    // The direction sent to the server: the raw intent, or (with physics) the
    // COLLISION-RESOLVED direction so the unchanged server tracks the collided path.
    let sendX = ix, sendZ = iz, sending = moving;

    // Movement mode (combat wave): the same entry rule the server applies —
    // a slide needs a sprint to begin and is over after SLIDE_MS; not moving
    // means walking. Speed comes from ONE table (combat_core.moveSpeedFor),
    // mirrored, so prediction and authority agree; a staggered player moves at
    // half speed on both sides.
    const nowMs = performance.now();
    const tick = ++this.fixedTicks;
    atlas.fixedTick = tick;
    // BUILD-NOW wave 1 (2026-09-12): the ONE request queue. Legacy latches
    // (the XR press, the touch jump button, probes) become requests here;
    // expired presses are dropped BEFORE anything can consume them, so a
    // press never fires late. TTLs count fixed ticks (the slide lesson).
    if (atlas.intentJump) { atlas.intents.request('jump', tick); atlas.intentJump = false; }
    if (atlas.slideRequested) { atlas.intents.request('slide', tick); atlas.slideRequested = false; }
    atlas.intents.expire(tick);
    // GUARD (build-now wave 4): mirror the held button to the server once per
    // change (setGuard); the raise's timestamp on the row IS the parry
    // attempt, judged there against the scheduled blow. A failed send is
    // retried next step; pre-publish bindings have no reducer and do nothing.
    if (atlas.guardHeld !== this.guardSent) {
      const r = conn.reducers as unknown as { setGuard?(a: { up: boolean }): Promise<void> };
      if (typeof r.setGuard === 'function') {
        this.guardSent = atlas.guardHeld;
        void r.setGuard({ up: atlas.guardHeld }).catch(() => { this.guardSent = null; });
      }
    }
    let m: MoveMode = moving ? (atlas.intentMode === MODE_SPRINT ? MODE_SPRINT : MODE_WALK) : MODE_WALK;
    if (this.mode === MODE_SLIDE && slideActive(nowMs - this.slideStartedMs)) {
      m = MODE_SLIDE; // a slide in progress is COMMITTED until its window ends
    } else if (atlas.intents.peek('slide') && (m === MODE_SPRINT || this.mode === MODE_SPRINT) && !atlas.guardHeld) {
      atlas.intents.consume('slide', tick);
      m = MODE_SLIDE; // the buffered tap starts the slide HERE (needs a sprint; else it waits out its TTL)
      this.slideStartedMs = nowMs;
    }
    // GUARD (build-now wave 4): a raised guard walks — never sprints, slides or dashes.
    if (atlas.guardHeld && m === MODE_SPRINT) m = MODE_WALK;
    // DASH (build-now wave 3): a double-tap's request starts a burst along the
    // tapped direction — the same entry rule the server applies (not
    // staggered, not afloat, DASH_CD_MS since the last dash began); the
    // burst is COMMITTED for DASH_MS and ignores steering, like the slide.
    const staggered = this.staggerLeftMs() > 0;
    if (this.mode === MODE_DASH && dashActive(nowMs - this.dashStartedMs)) {
      m = MODE_DASH;
    } else if (atlas.intents.peek('dash')) {
      const req = atlas.intents.peek('dash')!;
      if (m === MODE_SLIDE || this.mode === MODE_SLIDE) atlas.intents.reject('dash', tick, 'mid-slide');
      else if (atlas.guardHeld) atlas.intents.reject('dash', tick, 'guard raised');
      else if (!dashAllowed(nowMs - this.dashStartedMs, staggered, this.physics.waterMode === 'swim')) {
        // Inside the cooldown or reeling: the press waits out its TTL (a tap
        // a few ticks early still dashes the moment the cooldown ends).
        if (staggered) atlas.intents.reject('dash', tick, 'staggered');
      } else {
        atlas.intents.consume('dash', tick);
        const dl = Math.hypot(req.dirX, req.dirZ);
        this.dashDirX = dl > 1e-6 ? req.dirX / dl : -Math.sin(atlas.localYaw);
        this.dashDirZ = dl > 1e-6 ? req.dirZ / dl : -Math.cos(atlas.localYaw);
        this.dashStartedMs = nowMs;
        m = MODE_DASH;
        cameraImpulse(-0.012, 0); // the launch dips the view a hair; the FOV widens through stepFeel
        sfx('sfx_jump', { gain: 0.22, rate: 1.35, cooldownMs: 200 });
      }
    }
    if (m === MODE_DASH) { ix = this.dashDirX; iz = this.dashDirZ; }
    // CROUCH (build-now wave 2): a Ctrl tap toggles the wish; the capsule
    // stands only where the standing capsule fits (physics.setCrouch refuses
    // under a ceiling and the wish is kept — retried every step); a sprint,
    // a slide, a dash or a jump stands the player first.
    if (atlas.intents.peek('crouch')) { atlas.intents.consume('crouch', tick); this.wantCrouch = !this.wantCrouch; }
    if (m === MODE_SPRINT || m === MODE_SLIDE || m === MODE_DASH || atlas.intents.peek('jump')) this.wantCrouch = false;
    if (this.physics.ready && this.physics.crouched !== this.wantCrouch) this.physics.setCrouch(this.wantCrouch);
    const crouched = this.physics.ready ? this.physics.crouched : this.wantCrouch;
    if (crouched && m !== MODE_SLIDE && m !== MODE_DASH) m = MODE_CROUCH;
    let jumpFired = false; // a launch this step rides the next move message as the jump flag
    // Interactive water: afloat, the avatar swims — no sprint, no slide (the
    // server moves a swimmer at the crawl; the physics module decided the float).
    if (this.physics.waterMode === 'swim') m = MODE_SWIM;
    this.mode = m;
    const speed = moveSpeedFor(m, m === MODE_SLIDE ? nowMs - this.slideStartedMs : -1, staggered);
    if (this.trace) this.trace.push({ t: nowMs, mode: m, v: speed });

    if (this.physics.ready) {
      // Jump: buffer the press (a jump hit slightly before landing still fires on
      // touchdown) and launch through the KCC — physics.jump() applies coyote time
      // itself, so stepping off a ledge doesn't eat the input. Runs BEFORE the move
      // so the launch velocity integrates this same tick.
      if (atlas.intents.peek('jump')) {
        // MANTLE first (build-now wave 6): a jump pressed while pushing INTO
        // a waist-high obstacle (0.7–1.45 m, standing room on top) climbs it
        // instead — the scan is the physics module's (a shape cast, a ray and
        // two overlap tests); the climb is a scripted rise-then-step through
        // the KCC at walk pace, so the server's walk integration keeps up.
        const fwd = moving ? -(ix * Math.sin(atlas.localYaw) + iz * Math.cos(atlas.localYaw)) : 0;
        if (fwd > 0.5 && !this.physics.mantling && this.physics.waterMode !== 'swim') {
          const scan = this.physics.mantleScan(ix, iz);
          if (scan && this.physics.beginMantle(scan)) atlas.intents.consume('jump', tick, 'mantle');
        }
        // Else a jump: fires now, or stays pending for its 9-tick TTL (the
        // 0.15 s buffer: a press just before landing fires on touchdown;
        // physics.jump() applies coyote time itself).
        if (atlas.intents.peek('jump') && this.physics.jump()) { atlas.intents.consume('jump', tick); jumpFired = true; }
      }
      // Rapier KCC: collide-and-slide on walls, stand on floors/terrain, climb steps.
      // Runs every tick (even idle) so gravity + grounding settle the avatar's Y.
      const r = this.physics.moveAvatar(moving ? ix : 0, moving ? iz : 0, speed, FIXED_DT);
      let fullyBlocked = false; // zero displacement — distinct from a muted sub-1/16 slide
      if (r) {
        atlas.localX = r.x;
        atlas.localZ = r.z;
        atlas.localY = r.feetY; // grounded feet (terrain or a structure floor)
        const movedLen = Math.hypot(r.movedX, r.movedZ);
        fullyBlocked = movedLen <= 1e-4;
        if (movedLen > 1e-4) {
          // Scale the sent intent by the ACTUAL displacement ratio (≤1), not
          // just the direction: the server integrates a normalized intent at
          // FULL speed, so wherever the KCC moves slower than open ground —
          // interior stairs above all — the server's analytic path ran METERS
          // ahead (through walls), and the reconcile dragged the player out of
          // the building (the stairs "glitched out of the interior" field
          // bug). Sub-unit intent integrates proportionally server-side (only
          // len>1 is clamped), so the server's step now equals the client's
          // real one. Quantized to eighths (round, mean-zero error) so the
          // ~30 Hz coalescer still batches steady climbs into one message.
          const ratio = Math.min(1, movedLen / (speed * FIXED_DT));
          const q = Math.round(ratio * 8) / 8;
          if (q > 0) {
            sendX = (r.movedX / movedLen) * q;
            sendZ = (r.movedZ / movedLen) * q;
            sending = true;
          } else {
            // Sub-1/16 slide = pressed into geometry. The old 0.125 FLOOR
            // overstated these 2-12× — hold a wall press for seconds and the
            // server walked INTO the wall band, then corrections dragged the
            // capsule after it (the stuck-in-the-wall + jagged-movement field
            // bug, 2026-08-15). A held server never drifts.
            sending = false;
          }
        } else sending = false; // fully blocked → don't advance the server past the wall
      }
      // Spawn-stuck watchdog: 1 s of fully-blocked intent → embedded-capsule
      // unstick; 3 s continuous → also try the roofed-interior escape; and a
      // 2 s idle scan catches a no-input spawn straight into a collider. The
      // grace window holds reconcile off until the realigning reducer lands.
      // Keyed to TRUE zero displacement, not !sending: a muted sub-1/16 wall
      // slide must never accrue stuckTicks (the 4 s any-class escalation would
      // teleport a player who is just hugging a wall).
      const blocked = moving && r !== null && fullyBlocked && r.grounded;
      this.stuckTicks = blocked ? this.stuckTicks + 1 : 0;
      this.wedgedTicks = blocked && this.physics.wedged ? this.wedgedTicks + 1 : 0;
      const scanDue = ++this.unstickScanTicks >= 120;
      if (scanDue) this.unstickScanTicks = 0;
      const tryEscape = this.stuckTicks > 0 && this.stuckTicks % 180 === 0;
      // The unstick watchdog STANDS DOWN inside a building interior: its
      // roofed-space escape heuristic reads a legitimate interior as "trapped
      // in a collider" and RELOCATED the player outside ("stuck for a second
      // then glitched out" — 2026-08-14 field bug). Indoors, a briefly
      // wedged capsule resolves against the solid shell geometry instead.
      if (this.unstickGraceTicks === 0 && atlas.interiorId === null && (this.stuckTicks === 60 || tryEscape || scanDue)) {
        this.tryUnstick(tryEscape);
      }
      // Any-collider-class escalation: 3 s of PINCHED contacts (opposing
      // normals — a state collide-and-slide can never resolve; a plain wall
      // press has one normal family and never trips this), or 4 s fully
      // blocked whatever the evidence. unstickAvatar(anyClass) then also
      // accepts terrain embeds + wedges as trapped — recovery stays under the
      // 5 s budget without ever teleporting a player who is just hugging a wall.
      if (this.unstickGraceTicks === 0 && atlas.interiorId === null && (this.wedgedTicks === 180 || this.stuckTicks === 240)) {
        const g = globalThis as unknown as { __unstick?: { wedged: number } };
        if (g.__unstick) g.__unstick.wedged = (g.__unstick.wedged ?? 0) + 1;
        this.tryUnstick(false, true);
      }
      // Clamp-fight = trapped-by-construction (the login-bounce, 2026-08-23):
      // the physics fight breaker has stood the terrain clamp down for a full
      // second because a collider overlaps the analytic ground where we stand
      // (a slab streamed in over the spawn). No amount of settling converges —
      // relocate NOW, any-class, instead of waiting for the idle scan.
      if (this.unstickGraceTicks === 0 && atlas.interiorId === null && this.physics.clampFight >= 60 && this.physics.clampFight % 60 === 0) {
        this.tryUnstick(false, true);
      }
      // INDOOR rescue: corrections can no longer target the wall band, but a
      // capsule already embedded there (a pre-fix save, or any residual path)
      // has no way out — the outdoor watchdogs stand down indoors by law and
      // depenetration can't resolve a deep embed. Project onto the legal
      // interior region, keep Y (same floor), realign the server row via the
      // bounded unstickNudge. Never relocates outside the building; a no-op
      // when the capsule already stands in legal space.
      if (this.unstickGraceTicks === 0 && atlas.interiorId !== null && (this.wedgedTicks === 60 || this.stuckTicks === 120)) {
        this.tryIndoorRescue();
      }
    } else {
      // Naive fallback (physics not ready yet — same integration as the server).
      if (moving) {
        atlas.localX += ix * speed * FIXED_DT;
        atlas.localZ += iz * speed * FIXED_DT;
      }
      if (atlas.intents.consume('jump', tick, 'no physics yet — sent as the jump flag')) jumpFired = true;
    }

    // Probe once: without the wave-2 moveSteps reducer (pre-publish bindings) keep the
    // original per-tick sends — coalescing against a one-step server would run at half speed.
    if (this.canCoalesce === null) {
      this.canCoalesce = typeof (conn.reducers as unknown as { moveSteps?: unknown }).moveSteps === 'function';
    }
    if (!this.canCoalesce) {
      if (sending || jumpFired) {
        atlas.inputSeq += 1;
        recordSend(this.recon, atlas.inputSeq, logicalX(this.recon, atlas.localX), logicalZ(this.recon, atlas.localZ), performance.now());
        this.noteSend();
        void conn.reducers
          .move({ inputSeq: atlas.inputSeq, moveX: sendX, moveZ: sendZ, jump: jumpFired, yaw: atlas.localYaw })
          .then(() => noteResolve(this.moveHealth))
          .catch((e: Error) => this.noteRejection(e)); // transient; the next tick re-sends — but COUNTED
      }
      return;
    }

    // Coalesce: accumulate fixed steps; flush every SEND_EVERY_TICKS (~30 Hz) and
    // immediately on a jump, a direction change (bounds server-path divergence), or
    // when movement stops (so the server lands exactly on the final position).
    if (sending || jumpFired) {
      const dirChanged = this.pendingSteps > 0 &&
        (Math.abs(sendX - this.pendingX) > 1e-3 || Math.abs(sendZ - this.pendingZ) > 1e-3);
      if (dirChanged) this.flushMove(conn);
      this.pendingSteps += 1;
      this.pendingX = sendX;
      this.pendingZ = sendZ;
      this.pendingJump = this.pendingJump || jumpFired;
      if (this.pendingJump || this.pendingSteps >= SEND_EVERY_TICKS) this.flushMove(conn);
    } else if (this.pendingSteps > 0) {
      this.flushMove(conn);
    } else if (Math.abs(atlas.localYaw - this.lastSentYaw) > IDLE_YAW_FLUSH_RAD && performance.now() - this.lastIdleYawFlushMs > IDLE_YAW_FLUSH_MS) {
      // COMBAT WAVE: the server judges a strike's FACING on the row's yaw, and
      // the row only learned the yaw with a move — a player who turned in place
      // and fired was judged on a stale heading. Flush a zero-step "move" that
      // carries the yaw (rate-limited; the server writes yaw + idle anim).
      this.lastIdleYawFlushMs = performance.now();
      this.pendingSteps = 1;
      this.pendingX = 0;
      this.pendingZ = 0;
      this.flushMove(conn);
    }
  }

  /** The avatar is parked (fly cam / space / a ride / the loading gate):
   *  buffered presses must not fire when it wakes. */
  private parkIntents(): void {
    this.pendingSteps = 0;
    this.pendingJump = false;
    atlas.intentJump = false;
    atlas.slideRequested = false;
    if (atlas.intents.size > 0) atlas.intents.clear(this.fixedTicks, 'parked');
  }

  /** Send the accumulated intent window as ONE moveSteps message (wave-2, ~30 Hz). */
  private flushMove(conn: NonNullable<typeof atlas.conn>): void {
    if (this.pendingSteps === 0) return;
    atlas.inputSeq += 1;
    recordSend(this.recon, atlas.inputSeq, logicalX(this.recon, atlas.localX), logicalZ(this.recon, atlas.localZ), performance.now()); // the LOGICAL position after these steps
    const r = conn.reducers as unknown as {
      moveSteps(a: { inputSeq: number; moveX: number; moveZ: number; jump: boolean; yaw: number; steps: number }): Promise<void>;
      moveSteps2?(a: { inputSeq: number; moveX: number; moveZ: number; jump: boolean; yaw: number; steps: number; mode: number }): Promise<void>;
    };
    this.noteSend();
    if (this.canMode === null) this.canMode = typeof r.moveSteps2 === 'function';
    const args = {
      inputSeq: atlas.inputSeq,
      moveX: this.pendingX,
      moveZ: this.pendingZ,
      jump: this.pendingJump,
      yaw: atlas.localYaw,
      steps: this.pendingSteps,
    };
    this.lastSentYaw = atlas.localYaw;
    // moveSteps2 carries the movement MODE (combat wave); pre-publish bindings
    // fall back to moveSteps (walk speed on both sides — still in agreement).
    void (this.canMode && r.moveSteps2 ? r.moveSteps2({ ...args, mode: this.mode }) : r.moveSteps(args))
      .then(() => noteResolve(this.moveHealth))
      .catch((e: Error) => this.noteRejection(e)); // transient; the next flush re-sends — but COUNTED
    this.pendingSteps = 0;
    this.pendingJump = false;
  }

  // ─── FixedSim: reconcile against authoritative self ─────────────────────────

  /** Full unstick path: physics relocation + mirror + reconcile grace + the
   *  bounded server realign. Shared by the watchdog and the __unstickNow probe hook. */
  private tryUnstick(escapeInterior: boolean, anyClass = false): { x: number; z: number } | null {
    // Client-side rate limit ABOVE the server's 4 s unstickNudge cooldown: a
    // relocation whose nudge lands inside the cooldown is rejected server-side,
    // the row stays in the trap, and the next reconcile drags the capsule back
    // in — an every-4-s relocation LOOP (observed live: 111 relocations, grace
    // permanently refreshed, reconcile never able to seat a real teleport).
    // Spacing relocations past the cooldown guarantees each nudge is accepted.
    const now = performance.now();
    if (now - this.lastUnstickMs < 5_000) return null;
    // The server accepts a nudge only within 24 m of ITS row — which lags the
    // capsule by the ack latency. Cap the relocation radius by that gap so the
    // realign is accepted by construction (a rejected nudge = a snap back).
    const me = atlas.conn?.identity ? atlas.conn.db.playerState.identity.find(atlas.conn.identity) : null;
    const rowGap = me ? Math.hypot(me.posX - atlas.localX, me.posZ - atlas.localZ) : 0;
    let spot = this.physics.unstickAvatar(escapeInterior, anyClass, Math.max(2, 24 - rowGap - 1));
    let rescue = false;
    if (!spot && (anyClass || escapeInterior) && now - this.lastRescueMs >= 46_000) {
      // THE CAGE ESCAPE (field report 2026-08-23): a structure footprint
      // wider than the 24 m nudge envelope has NO clear ground the bounded
      // search can reach — the watchdog relocated pad→pad forever while the
      // player stood trapped on a 40 m building pad. Retry at the server's
      // RESCUE envelope (140 m / 45 s — slower than walking, exploit-free).
      spot = this.physics.unstickAvatar(escapeInterior, anyClass, Math.max(2, 140 - rowGap - 1));
      if (spot) { rescue = true; this.lastRescueMs = now; }
    }
    if (!spot) return null;
    this.lastUnstickMs = now;
    const fromX = atlas.localX, fromZ = atlas.localZ;
    atlas.localX = spot.x;
    atlas.localZ = spot.z;
    this.stuckTicks = 0;
    this.wedgedTicks = 0;
    this.sendNudge(spot.x, spot.z, fromX, fromZ, rescue);
    return spot;
  }

  /** Nudge bookkeeping (probe surface `__unstick.nudges`). */
  private nudges: NudgeStats = { sent: 0, accepted: 0, rejected: 0, reverted: 0, timedOut: 0, lastError: '' };

  /**
   * Realign the server row after a local relocation — and make the outcome
   * EXPLICIT (the move-then-snap-back field bug, 2026-08-22): the nudge used
   * to be fire-and-forget under a fixed 4 s reconcile grace; a nudge the
   * server rejected (24 m envelope measured from a LAGGING row, or the 4 s
   * cooldown) or one stalled on a loaded link left the row behind, and once
   * the grace ran out reconcile marched the player back to it — then forward
   * again when a late nudge finally landed. Now: reconcile stays off until
   * the nudge SETTLES (bounded at 10 s); a rejection reverts the relocation
   * at once (server authority, no drag), a timeout falls back to reconcile.
   */
  private sendNudge(x: number, z: number, fromX: number, fromZ: number, rescue = false): void {
    this.unstickGraceTicks = 600; // bounded hold while the nudge is in flight (10 s)
    const rr = atlas.conn?.reducers as unknown as {
      unstickNudge?: (a: { posX: number; posZ: number }) => Promise<void>;
      unstickRescue?: (a: { posX: number; posZ: number }) => Promise<void>;
    } | undefined;
    const send = rescue && rr && typeof rr.unstickRescue === 'function' ? rr.unstickRescue : rr?.unstickNudge;
    if (!rr || typeof send !== 'function') { this.unstickGraceTicks = 240; return; }
    this.nudges.sent++;
    const g = globalThis as unknown as { __unstick?: { nudges?: NudgeStats } };
    if (g.__unstick) g.__unstick.nudges = this.nudges;
    let settled = false;
    const settle = (ok: boolean, err?: unknown): void => {
      if (settled) return;
      settled = true;
      if (ok) { this.nudges.accepted++; this.unstickGraceTicks = Math.min(this.unstickGraceTicks, 60); return; } // the row lands within a few ticks
      this.nudges.rejected++;
      this.nudges.lastError = String((err as Error)?.message ?? err).slice(0, 80);
      Logger.warn('Avatar', `unstick nudge rejected (${this.nudges.lastError}) — reverting the relocation`);
      // The server refused the realign: the relocation is void. Go back NOW
      // (a clean step, not a reconcile drag) and let the row lead again.
      this.nudges.reverted++;
      atlas.localX = fromX;
      atlas.localZ = fromZ;
      if (this.physics.ready) this.physics.teleportAvatar(fromX, fromZ, true);
      this.unstickGraceTicks = 0;
    };
    try {
      send({ posX: x, posZ: z }).then(() => settle(true), (e: unknown) => settle(false, e));
    } catch (e) { settle(false, e); }
    window.setTimeout(() => { if (!settled) { settled = true; this.nudges.timedOut++; } }, 10_000); // a stalled link: reconcile resumes when the grace ends
  }

  /** Indoor wall-band rescue: project an embedded capsule back into the
   *  interior's legal region (clampIndoor), keep Y, realign the server row.
   *  Small and local by construction — the projection delta is at most the
   *  wall-sheet thickness, never a relocation outside the building. */
  private tryIndoorRescue(): void {
    if (atlas.interiorId === null) return;
    const b = enterables.get(atlas.interiorId);
    if (!b) return;
    const p = clampIndoor(b, atlas.localX, atlas.localZ);
    if (!p.clamped) return; // legal space — wedge resolves against solid interior geometry
    const now = performance.now();
    if (now - this.lastUnstickMs < 5_000) return; // stay past the server nudge cooldown
    this.lastUnstickMs = now;
    const fromX = atlas.localX, fromZ = atlas.localZ;
    atlas.localX = p.x;
    atlas.localZ = p.z;
    if (this.physics.ready) this.physics.teleportAvatar(p.x, p.z, true);
    this.stuckTicks = 0;
    this.wedgedTicks = 0;
    this.sendNudge(p.x, p.z, fromX, fromZ);
  }

  private reconcileRuns = 0; // probe diagnostics

  private reconcile(): void {
    this.reconcileRuns++;
    (globalThis as unknown as { __reconcileDbg?: () => { runs: number; grace: number; local: number[]; recon: ReconcileState['stats']; inFlight: number } }).__reconcileDbg ??=
      () => ({ runs: this.reconcileRuns, grace: this.unstickGraceTicks, local: [Math.round(atlas.localX), Math.round(atlas.localZ)], recon: { ...this.recon.stats }, inFlight: this.recon.history.length });
    const conn = atlas.conn;
    if (!conn || !conn.identity || atlas.currentWorldId === null) return;
    const self = conn.db.playerState.identity.find(conn.identity);
    if (!self || self.worldId !== atlas.currentWorldId) return;

    // Movement-health watchdog: the row's inputSeq is the server's ack.
    this.observeAck(self.inputSeq);

    // Unstick grace: corrections would drag the capsule back into the collider
    // it just escaped — hold off until the unstickNudge realigns the row. The
    // countdown lives HERE (not fixedMovement) because reconcile runs ungated —
    // fixedMovement skips under the loading gate / fly / space, which would
    // freeze the grace and suppress reconcile forever.
    if (this.unstickGraceTicks > 0) { this.unstickGraceTicks--; return; }

    // Space flight / fly cam own position — the avatar capsule parks. Without
    // this gate every flight tick saw a hundreds-of-km "divergence" and
    // hard-snap teleported the capsule (setTranslation + a full world.step),
    // the storm behind the wasm borrow poisoning (2026-07-30 console).
    if (atlas.spaceFlight || atlas.flyMode || atlas.hoverRide || atlas.skyFlight) return;

    // LAG-AWARE (the walking-drag fix, lib/move_reconcile.ts): the row is
    // compared with the position the client PREDICTED for the acknowledged
    // input sequence, never with the current position while inputs are in
    // flight — round-trip latency alone can no longer drag the avatar back.
    // Only a real disagreement (speed clamp, held position, unstick nudge,
    // teleport) becomes a correction, applied as a delta to the current spot.
    const c = decideCorrection(this.recon, self.inputSeq, self.posX, self.posZ, logicalX(this.recon, atlas.localX), logicalZ(this.recon, atlas.localZ), atlas.inputSeq,
      atlas.interiorId !== null ? 0.8 : RECONCILE_SNAP_M, RECONCILE_HARD_SNAP_M, performance.now());
    if (c.mode === 'hard') {
      // Teleport-scale divergence: instant snap (enterWorld, debug_teleport).
      atlas.localX = self.posX;
      atlas.localZ = self.posZ;
      if (this.physics.ready) this.physics.teleportAvatar(self.posX, self.posZ);
      return;
    }
    // Visual catch-up: a disagreement re-based the logical path ONCE (inside
    // decideCorrection); the rendered avatar now closes the debt a fraction
    // per tick — the smooth correction, applied to the debt, never re-applied.
    const step = applySmoothing(this.recon);
    if (step.dx !== 0 || step.dz !== 0) {
      const dx = step.dx, dz = step.dz;
      // Indoors the threshold loosens to 0.8 m: each smooth correction is a
      // TELEPORT into whatever geometry stands in the way (stairs, walls) and
      // depenetration fights it — a sub-metre divergence inside a building is
      // invisible to anyone while the constant micro-teleports were the
      // residual stairs-eject churn. Outdoors keeps the tight 0.25 m.
      // SMOOTH correction (2026-07-21): the old hard snap was the visible
      // terrain rubber-band — the client KCC catches on steep seg-48 mesh
      // facets (>62° locally) that the server's analytic movement glides
      // over, diverging a few tens of cm; pulling 30 %/tick erases the pop
      // while still converging in ~6 ticks (~100 ms).
      atlas.localX += dx;
      atlas.localZ += dz;
      // Indoors the correction target is CLAMPED to the legal walkable region
      // (core rect + door corridor): teleportAvatar is collision-blind, and a
      // target inside the 1.6 m airtight wall band embeds the capsule in a box
      // collider it can never depenetrate from — with both unstick watchdogs
      // standing down indoors, that wedge was permanent (stuck-in-the-wall
      // field bug, 2026-08-15). The server has no walls; its position is
      // advisory here, so projecting it back into legal space loses nothing.
      if (atlas.interiorId !== null) {
        const b = enterables.get(atlas.interiorId);
        if (b) {
          const p = clampIndoor(b, atlas.localX, atlas.localZ);
          atlas.localX = p.x;
          atlas.localZ = p.z;
        }
      }
      // keepY: a smooth correction is an XZ nudge — never re-seat the height
      // (that was the jump-teleports-to-ground-floor bug).
      if (this.physics.ready) this.physics.teleportAvatar(atlas.localX, atlas.localZ, true);
    }
  }

  // ─── Render frame: apply local transform + interpolate remotes ──────────────

  private interpolate(dt: number): void {
    // Apply the local predicted transform to the camera rig — UNLESS the admin fly
    // camera owns it (AtlasAdminEditorModule drives localBody directly while flying).
    if (!atlas.flyMode && !atlas.spaceFlight) {
      this.localBody.position.set(atlas.localX, atlas.localY, atlas.localZ);
      this.localBody.rotation.y = atlas.localYaw;
      // COMBAT WAVE feel: the camera's pose is the input pitch PLUS the feel
      // offsets (kick spring, optional shake), re-applied absolutely every
      // frame so nothing accumulates; a sprint widens the FOV a few degrees
      // and a slide drops the eye. The XR session and the ship own their own
      // cameras — never touch those.
      if (!atlas.xrPresenting && !atlas.skyFlight) {
        const moving = atlas.intentX !== 0 || atlas.intentZ !== 0;
        const off = stepFeel(dt, performance.now() / 1000,
          this.mode === MODE_SPRINT && moving ? SPRINT_FOV_DEG : this.mode === MODE_DASH ? DASH_FOV_DEG : 0,
          this.mode === MODE_SLIDE ? SLIDE_EYE_DROP_M : this.mode === MODE_CROUCH ? CROUCH_EYE_DROP_M : 0);
        // Third person: my own body ducks with the capsule (the cheap crouch
        // every rig-less prototype uses; remotes read the same 'crouch' state).
        if (this.localProxy) {
          const cs = this.mode === MODE_CROUCH ? CROUCH_BODY_SCALE : 1;
          const o = this.localProxy.object;
          if (Math.abs(o.scale.y - cs) > 1e-3) o.scale.y += (cs - o.scale.y) * Math.min(1, dt * 12);
        }
        this.camera.rotation.set(atlas.localPitch + off.pitch, off.yaw, off.roll);
        this.poseCamera(dt, atlas.localPitch + off.pitch, EYE_HEIGHT - off.eyeDrop);
        const fov = this.baseFov + off.fov;
        if (Math.abs(this.camera.fov - fov) > 0.01) { this.camera.fov = fov; this.camera.updateProjectionMatrix(); }
      }
    }

    // Buffered snapshot interpolation (the Valve model): render each remote slightly
    // in the past — 2× its OBSERVED update interval (clamped 50..250 ms) — and lerp
    // between the two bracketing authoritative snapshots. One late/dropped update
    // still leaves two valid snapshots, so motion stays smooth where the old
    // exponential chase rubber-banded. Build the body lazily from its portable
    // appearance (origin at feet, y = 0).
    const now = performance.now();
    const k = 1 - Math.pow(0.001, dt); // fallback smoothing (sparse-snapshot case)
    for (const r of atlas.remotes.values()) {
      let proxy = this.remoteProxies.get(r.identityHex);
      if (!proxy) {
        proxy = buildAvatar(r.appearance ?? defaultAppearance());
        proxy.object.position.set(r.targetX, 0, r.targetZ); // Y ground-seated below, first frame
        this.scene.add(proxy.object);
        if (this.appRef) void warmThenShow(this.appRef, proxy.object); // R4: a new avatar's skinned pipelines link off the frame
        this.remoteProxies.set(r.identityHex, proxy);
        r.mesh = proxy.object;
        this.wearModel(r.identityHex, proxy, r.appearance ?? null);
      }
      const obj = proxy.object;
      const snaps = r.snaps;
      const delay = Math.min(250, Math.max(50, r.snapIntervalMs * 2));
      const renderT = now - delay;
      let interpolated = false;
      if (snaps.length >= 2 && snaps[0].t <= renderT) {
        // Find the bracketing pair (ring is small — linear scan from the newest).
        for (let i = snaps.length - 1; i >= 1; i--) {
          const a = snaps[i - 1];
          const b = snaps[i];
          if (a.t <= renderT && renderT <= b.t) {
            const f = b.t > a.t ? (renderT - a.t) / (b.t - a.t) : 1;
            obj.position.x = a.x + (b.x - a.x) * f;
            obj.position.z = a.z + (b.z - a.z) * f;
            obj.rotation.y = lerpYaw(a.yaw, b.yaw, f);
            interpolated = true;
            break;
          }
        }
        if (!interpolated && renderT > snaps[snaps.length - 1].t) {
          // Past the newest snapshot (a stall or the remote stopped): hold the latest
          // authoritative pose rather than extrapolating into walls (Valve caps
          // extrapolation at 0.25 s; holding is the conservative equivalent here).
          const b = snaps[snaps.length - 1];
          obj.position.x = b.x;
          obj.position.z = b.z;
          obj.rotation.y = b.yaw;
          interpolated = true;
        }
      }
      if (!interpolated) {
        // Not enough history yet — the original framerate-independent chase.
        obj.position.x += (r.targetX - obj.position.x) * k;
        obj.position.z += (r.targetZ - obj.position.z) * k;
        obj.rotation.y = r.targetYaw;
      }
      // Seat on the RENDERED ground at the interpolated (x,z). Was a hard
      // `y = 0`, which floated/sank every remote on elevated terrain — and the
      // server can't help: movement authority is 2D (player_state.pos_y is a
      // constant 1, never integrated), so client-side grounding is the truth.
      // EXCEPT vehicles (the vehicle wave): a 'fly:' remote rides its
      // authoritative posY (flyMove DOES integrate Y), a 'ride:' remote rides
      // its own — clamping either to the dirt rendered a ship overhead as a man
      // standing in a field.
      const conn = atlas.conn;
      const groundR = conn ? groundYIn(conn, atlas.currentWorldId, obj.position.x, obj.position.z) : 0;
      if (r.animState.startsWith('fly:')) {
        obj.position.y += (Math.max(groundR, r.targetY) - obj.position.y) * k;
      } else if (r.animState.startsWith('ride:')) {
        // A RIDER JUMPS (2026-09-16). vehicleMove carries posY now, so a remote
        // who launches off a crest is drawn in the air instead of sliding along
        // the ground — the most visible multiplayer artefact in the system. The
        // ground is the FLOOR, not the seat: the authoritative height already
        // includes the skimmer's hover gap, and a rider crossing terrain the
        // viewer has not streamed would otherwise sink into it.
        obj.position.y += (Math.max(groundR + RIDE_DECK_M, r.targetY) - obj.position.y) * k;
      } else {
        obj.position.y = groundR;
      }
      // Crouch (build-now wave 2): a crouched remote ducks its body.
      const cs = r.animState === 'crouch' ? CROUCH_BODY_SCALE : 1;
      if (Math.abs(obj.scale.y - cs) > 1e-3) obj.scale.y += (cs - obj.scale.y) * k;
    }

    // Reap proxies for remotes the network layer dropped (left a world, etc.).
    for (const [hex, proxy] of this.remoteProxies) {
      if (!atlas.remotes.has(hex)) {
        this.scene.remove(proxy.object);
        this.releaseWorn(proxy);
        proxy.dispose();
        this.remoteProxies.delete(hex);
      }
    }
  }

  /** Rebuild one remote's body from its (now-updated) portable appearance. */
  private rebuildRemote(hex: string): void {
    const r = atlas.remotes.get(hex);
    const old = this.remoteProxies.get(hex);
    if (!r || !old) return; // not visible yet; interpolate() will build it
    const next = buildAvatar(r.appearance ?? defaultAppearance());
    next.object.position.copy(old.object.position);
    next.object.rotation.y = r.targetYaw;
    this.scene.add(next.object);
    if (this.appRef) void warmThenShow(this.appRef, next.object); // R4
    this.scene.remove(old.object);
    this.releaseWorn(old);
    old.dispose();
    this.remoteProxies.set(hex, next);
    r.mesh = next.object;
    this.wearModel(hex, next, r.appearance ?? null);
  }

  /**
   * A remote whose appearance carries a scanned-model attachment wears that
   * GLB: primitives hide, a clone of the shared cache root rides the SAME
   * proxy object (interpolation untouched). Async + stale-guarded; a missing
   * catalog row simply leaves the primitive body standing.
   */
  private wearModel(hex: string, proxy: AvatarProxy, appearance: { attachments?: Record<string, string> } | null, attempt = 0): void {
    const key = modelKeyOf(appearance?.attachments ?? null);
    if (!key || !this.assets || !atlas.conn) return;
    const assetId = prefabAssetId(atlas.conn, key);
    if (assetId === null) {
      // The prefab catalog subscription may not have applied when this remote
      // built — without a retry the player wears primitives forever.
      if (attempt < 30) window.setTimeout(() => {
        if (this.remoteProxies.get(hex) === proxy) this.wearModel(hex, proxy, appearance, attempt + 1);
      }, 2000);
      return;
    }
    void this.assets.load(assetId).then((shared) => {
      if (this.remoteProxies.get(hex) !== proxy) { this.assets?.dispose(assetId); return; } // rebuilt meanwhile
      proxy.object.traverse((o) => { if ((o as THREE.Mesh).isMesh) o.visible = false; });
      const c = shared.clone();
      c.name = `player-model:${key}`;
      proxy.object.add(c);
      // Remember the cache ref so the proxy's teardown can hand it back (each
      // remote entering AOI / rebuild / world hop used to pin the GLB forever).
      this.wornAssets.set(proxy, assetId);
    }).catch(() => { /* asset unavailable — primitives remain */ });
  }

  /**
   * The scroll-wheel camera (2026-09-09): first person at camBack 0, a
   * third-person orbit around the head past FIRST_PERSON_MAX, the hovercraft
   * chase from atlas.hoverCamBack while riding. A ray from the head toward the
   * camera (Rapier statics + the analytic ground) keeps it out of walls and
   * hills; the drawn distance eases toward the target.
   */
  private poseCamera(dt: number, pitch: number, eye: number): void {
    const hover = atlas.hoverRide;
    let target = hover ? atlas.hoverCamBack : atlas.camBack;
    const third = hover || isThirdPerson(target);
    if (third && target > 0) {
      const from = this._camFrom;
      from.x = atlas.localX; from.y = atlas.localY + eye; from.z = atlas.localZ;
      const dir = hover ? hoverCameraRayDir(atlas.localYaw) : cameraRayDir(atlas.localYaw, pitch); // toward where the pose will put the camera
      let hit: number | null = null;
      const ph = this.physics.ready ? this.physics.rayHit(from, dir, target + CAMERA_PAD_M) : null;
      if (ph) hit = ph.dist;
      const conn = atlas.conn;
      if (conn) {
        const wid = atlas.currentWorldId;
        const g = rayGroundHit((x, z) => groundYIn(conn, wid, x, z), from.x, from.y, from.z, dir.x, dir.y, dir.z, target + CAMERA_PAD_M, 0.5);
        if (g && (hit === null || g.t < hit)) hit = g.t;
      }
      target = collideCamBack(target, hit);
    }
    this.camBackDrawn = target === 0 && this.camBackDrawn < 0.05 ? 0 : smoothCamBack(this.camBackDrawn, target, dt);
    if (target === 0 && this.camBackDrawn < 0.02) this.camBackDrawn = 0;
    const pose = hover ? hoverCameraPose(this.camBackDrawn, eye) : footCameraPose(this.camBackDrawn, pitch, eye);
    this.camera.position.set(pose.x, pose.y, pose.z);
    this.setThirdPerson(third && this.camBackDrawn >= CAMERA_MIN_BACK_M * 0.5);
  }

  /** Show my body + hide the view model in third person; the reverse in first. */
  private setThirdPerson(on: boolean): void {
    if (on && (!this.localProxy || this.localProxyAppearance !== (atlas.localAvatar?.appearance ?? null))) this.buildLocalProxy();
    if (this.localProxy) this.localProxy.object.visible = on;
    const vm = this.camera.getObjectByName('weapon-view-model');
    if (vm && vm.visible === on) vm.visible = !on;
    this.thirdPerson = on;
  }

  private buildLocalProxy(): void {
    this.disposeLocalProxy();
    const appearance = atlas.localAvatar?.appearance ?? defaultAppearance();
    const proxy = buildAvatar(appearance);
    proxy.object.name = 'local-body';
    proxy.object.position.set(0, 0, 0); // feet at the rig origin (localY is the feet)
    proxy.object.visible = false;
    this.localBody.add(proxy.object);
    if (this.appRef) void warmThenShow(this.appRef, proxy.object);
    this.localProxy = proxy;
    this.localProxyAppearance = atlas.localAvatar?.appearance ?? null;
    // Wear my scanned model like a remote does (stale-guarded on the proxy).
    const key = modelKeyOf(appearance.attachments ?? null);
    if (key && this.assets && atlas.conn) {
      const assetId = prefabAssetId(atlas.conn, key);
      if (assetId !== null) {
        void this.assets.load(assetId).then((shared) => {
          if (this.localProxy !== proxy) { this.assets?.dispose(assetId); return; }
          proxy.object.traverse((o) => { if ((o as THREE.Mesh).isMesh) o.visible = false; });
          const c = shared.clone();
          c.name = `player-model:${key}`;
          proxy.object.add(c);
          this.localWornId = assetId;
        }).catch(() => { /* primitives remain */ });
      }
    }
  }

  private disposeLocalProxy(): void {
    if (!this.localProxy) return;
    this.localBody.remove(this.localProxy.object);
    if (this.localWornId !== null) { this.assets?.dispose(this.localWornId); this.localWornId = null; }
    this.localProxy.dispose();
    this.localProxy = null;
  }

  private clearRemotes(): void {
    for (const proxy of this.remoteProxies.values()) {
      this.scene.remove(proxy.object);
      this.releaseWorn(proxy);
      proxy.dispose();
    }
    this.remoteProxies.clear();
  }

  /** Asset refs held by remote proxies' worn GLBs (released with the proxy). */
  private readonly wornAssets = new Map<AvatarProxy, bigint>();
  private releaseWorn(proxy: AvatarProxy): void {
    const id = this.wornAssets.get(proxy);
    if (id !== undefined) { this.wornAssets.delete(proxy); this.assets?.dispose(id); }
  }

  /** ms of stagger left on MY player_action row (combat wave), 0 when none. */
  private staggerLeftMs(): number {
    const conn = atlas.conn;
    if (!conn?.identity) return 0;
    const db = conn.db as unknown as { playerAction?: { identity: { find(i: unknown): { staggeredUntil: { microsSinceUnixEpoch: bigint } } | undefined } } };
    const a = db.playerAction?.identity.find(conn.identity);
    if (!a) return 0;
    return Number(a.staggeredUntil.microsSinceUnixEpoch / 1000n) - Date.now();
  }

  /** Probe/diag: the current movement mode + stagger (combat wave). */
  get moveMode(): {
    mode: MoveMode; slideElapsedMs: number; staggerLeftMs: number; canMode: boolean | null; ticks: number;
    crouched: boolean; wantCrouch: boolean; dashElapsedMs: number; mantling: boolean; mantleWhy: string; guard: boolean;
  } {
    return {
      mode: this.mode, slideElapsedMs: this.mode === MODE_SLIDE ? performance.now() - this.slideStartedMs : -1,
      staggerLeftMs: Math.max(0, this.staggerLeftMs()), canMode: this.canMode, ticks: this.fixedTicks,
      // Build-now waves 2–6 (probe levers).
      crouched: this.physics.crouched, wantCrouch: this.wantCrouch,
      dashElapsedMs: this.mode === MODE_DASH ? performance.now() - this.dashStartedMs : -1,
      mantling: this.physics.mantling, mantleWhy: this.physics.mantleWhy, guard: atlas.guardHeld,
    };
  }

  get cameraRef(): THREE.PerspectiveCamera {
    return this.camera;
  }

  dispose(): void {
    this.ac.abort();
    this.clearRemotes();
  }
}

/** Shortest-arc yaw interpolation (avoids the long-way spin across the ±π seam). */
function lerpYaw(a: number, b: number, f: number): number {
  let d = b - a;
  while (d > Math.PI) d -= Math.PI * 2;
  while (d < -Math.PI) d += Math.PI * 2;
  return a + d * f;
}
